
import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { Loader2, Eye, EyeOff, Shield, Mail, MessageCircle } from "lucide-react";
import { decideAuthDestination } from "@/utils/authDestination";
import { supabase } from "@/integrations/supabase/client";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdminLogin, setIsAdminLogin] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [resending, setResending] = useState(false);
  const { login, isAuthenticated, user, profileLoaded } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect só após o profile carregar (evita rebote para /planos com plan: null transitório)
  useEffect(() => {
    if (!isAuthenticated || !user || !profileLoaded) return;
    const dest = decideAuthDestination(user, location.state?.from?.pathname);
    console.log("🔐 LoginPage - profile carregado, redirecionando para:", dest, {
      plan: user.plan,
      isSubscribed: user.isSubscribed,
      role: user.role,
    });
    navigate(dest, { replace: true });
  }, [isAuthenticated, user, profileLoaded, navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        title: "Erro",
        description: "Por favor, preencha todos os campos.",
        variant: "destructive",
      });
      return;
    }

    // Input validation
    const trimmedEmail = email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailRegex.test(trimmedEmail)) {
      toast({
        title: "Erro",
        description: "Por favor, insira um email válido.",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Erro",
        description: "A senha deve ter pelo menos 6 caracteres.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setNeedsConfirmation(false);

    try {
      console.log("Attempting login for:", trimmedEmail);
      const result = await login(trimmedEmail, password);
      
      if (result && 'error' in result && result.error) {
        console.error("Login error:", result.error);
        const msg = (result.error.message || "").toLowerCase();
        const code = (result.error as any).code || (result.error as any).error_code || "";
        if (msg.includes("email not confirmed") || msg.includes("not confirmed") || code === "email_not_confirmed") {
          setNeedsConfirmation(true);
          toast({
            title: "E-mail não confirmado",
            description: "Confirme seu e-mail para entrar. Reenvie o link abaixo se precisar.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Erro no login",
            description: result.error.message || "Verifique suas credenciais e tente novamente.",
            variant: "destructive",
          });
        }
        return;
      }

      // Success toast
      toast({
        title: "Sucesso!",
        description: `Bem-vindo${isAdminLogin ? " ao painel administrativo" : ""}!`,
      });

      console.log("✅ Login bem-sucedido!");

      // Sem setTimeout: o useEffect acima redireciona assim que profileLoaded=true.
      
    } catch (error: any) {
      console.error("Login error:", error);
      toast({
        title: "Erro no login",
        description: error.message || "Erro interno. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast({ title: "E-mail inválido", description: "Informe seu e-mail no campo acima.", variant: "destructive" });
      return;
    }
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: trimmed,
        options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) throw error;
      toast({ title: "E-mail reenviado!", description: "Verifique sua caixa de entrada e o SPAM." });
    } catch (err: any) {
      toast({ title: "Não foi possível reenviar", description: err?.message || "Tente novamente.", variant: "destructive" });
    } finally {
      setResending(false);
    }
  };

  const openWhatsAppSupport = () => {
    const msg = `Olá! Não consigo confirmar meu e-mail no IA Palpite Guru. Meu e-mail: ${email || "(informar)"}`;
    window.open(`https://wa.me/5573981988263?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleAdminLoginChange = (checked: boolean) => {
    setIsAdminLogin(checked);
  };

  const handleNavigateHome = () => {
    try {
      navigate("/");
    } catch (error) {
      console.error("Navigation error:", error);
      window.location.href = "/";
    }
  };

  const handleNavigateRegister = () => {
    try {
      navigate("/cadastro");
    } catch (error) {
      console.error("Navigation error:", error);
      window.location.href = "/cadastro";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-gradient-to-r from-purple-500 to-blue-500 p-3 rounded-lg">
              {isAdminLogin ? (
                <Shield className="w-8 h-8 text-white" />
              ) : (
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                  <span className="text-purple-500 font-bold text-lg">P</span>
                </div>
              )}
            </div>
          </div>
          <CardTitle className="text-2xl text-center">
            {isAdminLogin ? "Acesso Administrativo" : "Entrar"}
          </CardTitle>
          <CardDescription className="text-center">
            {isAdminLogin 
              ? "Faça login com suas credenciais de administrador"
              : "Entre na sua conta para gerar palpites com IA"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                required
                autoComplete="email"
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Digite sua senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  required
                  autoComplete="current-password"
                  className="w-full pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={togglePasswordVisibility}
                  disabled={isLoading}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="admin-login"
                checked={isAdminLogin}
                onCheckedChange={handleAdminLoginChange}
                disabled={isLoading}
              />
              <Label 
                htmlFor="admin-login" 
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2"
              >
                <Shield className="w-4 h-4" />
                Login de Administrador
              </Label>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-gradient" 
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Entrando...
                </>
              ) : (
                `Entrar${isAdminLogin ? " como Admin" : ""}`
              )}
            </Button>
          </form>

          {needsConfirmation && (
            <div className="mt-4 rounded-lg border-2 border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 p-4 space-y-3">
              <div className="flex items-start gap-2">
                <Mail className="w-5 h-5 text-yellow-700 dark:text-yellow-300 mt-0.5 shrink-0" />
                <div className="text-sm text-yellow-900 dark:text-yellow-100">
                  <p className="font-semibold">Seu e-mail ainda não foi confirmado.</p>
                  <p className="mt-1">Verifique sua caixa de entrada e a pasta de <strong>SPAM</strong>. O e-mail tem o título <em>"IA PALPITE GURU, IMPORTANTE: Confirme seu Email!"</em>.</p>
                </div>
              </div>
              <Button
                type="button"
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white"
                onClick={handleResendConfirmation}
                disabled={resending}
              >
                {resending ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Reenviando...</>
                ) : (
                  <><Mail className="mr-2 h-4 w-4" /> Reenviar e-mail de confirmação</>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full border-green-500 text-green-700 hover:bg-green-50"
                onClick={openWhatsAppSupport}
              >
                <MessageCircle className="mr-2 h-4 w-4" /> Falar com suporte no WhatsApp
              </Button>
            </div>
          )}


          <div className="mt-4 text-center">
            <button
              onClick={() => navigate("/esqueci-senha")}
              className="text-sm text-primary hover:underline font-medium cursor-pointer"
              disabled={isLoading}
            >
              Esqueceu a senha?
            </button>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full mt-4 font-semibold"
            onClick={handleNavigateRegister}
            disabled={isLoading}
          >
            Cadastre-se
          </Button>

          <div className="mt-4 text-center">
            <button
              onClick={handleNavigateHome}
              className="text-sm text-muted-foreground hover:text-primary transition-colors cursor-pointer"
              disabled={isLoading}
            >
              ← Voltar ao início
            </button>
          </div>

          {/* Mobile-specific help text */}
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg md:hidden">
            <p className="text-xs text-blue-700 dark:text-blue-300 text-center">
              💡 <strong>Dica:</strong> Se o login não funcionar, verifique se confirmou seu email na caixa de entrada.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
